import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplicationProperties } from "../../../../../../hooks/useApplicationProperties"
import { digitalManagerApi } from "../../../../../../services/api"
import { FieldRow, FormPage } from "../../../../../components/FormPage"
import PropertyName from "../../../../../components/forms/PropertyName"
import PropertyValue from "../../../../../components/forms/PropertyValue"
import { ApplicationPermissionUserGet } from "../../../../../models/ApplicationPermissionUser"
import {
  ApplicationPropertyDetail,
  ApplicationPropertyGet,
  ApplicationPropertyPossibleValue,
  ApplicationPropertyTypeEnum,
  ApplicationPropertyValPostUser,
} from "../../../../../models/ApplicationProperty"

const validationSchema = z.object({
  propertyName: z.string().min(1, "Property name is required"),
  propertyValue: z.string().min(1, "Property value is required"),
  permissionId: z.string(),
  userId: z.string(),
})

export function UserApplicationPermissionPropertyCreate() {
  const { userId, applicationId, permissionId } = useParams()
  const { applicationProperties, isLoadingApplicationProperties } =
    useApplicationProperties(applicationId, false, true)

  const [
    userApplicationPermissionPropertiesIds,
    setUserApplicationPermissionPropertiesIds,
  ] = useState<ApplicationPropertyDetail[]>([])
  const navigate = useNavigate()
  const returnURL = `/users/${userId}/applications/${applicationId}/permissions/${permissionId}#properties`

  const [propertyType, setPropertyType] = useState<ApplicationPropertyTypeEnum>(
    ApplicationPropertyTypeEnum.String
  )
  const [propertyPossibleValue, setPropertyPossibleValue] =
    useState<ApplicationPropertyPossibleValue>({ value: "", label: "" })

  const propertyPossibleValues: { [key: string]: ApplicationPropertyPossibleValue[] } =
    useMemo(() => {
      const propertyPossibleValuesDict: {
        [key: string]: ApplicationPropertyPossibleValue[]
      } = {}
      if (applicationProperties?.length) {
        applicationProperties.forEach((property: ApplicationPropertyGet) => {
          propertyPossibleValuesDict[property.type] = property.possibleValues
        })
      }
      return propertyPossibleValuesDict
    }, [applicationProperties])

  useEffect(() => {
    if (userId && applicationId && permissionId) {
      digitalManagerApi
        .get<ApplicationPermissionUserGet>(
          `/users/${userId}/applications/${applicationId}/permissions/${permissionId}`
        )
        .then((res) => {
          if (res.data?.properties.length) {
            const filteredProperties = res.data.properties.map(
              (property: ApplicationPropertyDetail) => ({
                name: property.name,
                value: property.value,
              })
            )
            setUserApplicationPermissionPropertiesIds(filteredProperties)
          }
        })
        .catch(() => {
          navigate(returnURL)
        })
    }
  }, [applicationId, navigate, returnURL, permissionId, userId])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid, isDirty },
    setValue,
  } = useForm<{ propertyName: string } & ApplicationPropertyValPostUser>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      propertyName: "",
      propertyValue: "",
      userId,
      permissionId,
    })
  }, [permissionId, reset, userId])

  const onSubmit = useCallback(
    async (values: { propertyName: string } & ApplicationPropertyValPostUser) => {
      try {
        const response = await digitalManagerApi.post(
          `/applications/${applicationId}/properties/${values.propertyName}/values`,
          {
            propertyValue: values.propertyValue,
            userId: values.userId,
            permissionId: values.permissionId,
          }
        )
        if (response.status === StatusCodes.CREATED) {
          toast.success("Property added to role's permission")
          navigate(returnURL)
        }
      } catch {
        toast.error("Cannot add property to user's permission")
      }
    },
    [applicationId, navigate, returnURL]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Property Name">
        <PropertyName<{ propertyName: string } & ApplicationPropertyValPostUser>
          control={control}
          setValue={setValue}
          applicationProperties={applicationProperties}
          setPropertyType={setPropertyType}
          setPropertyPossibleValue={setPropertyPossibleValue}
          isLoadingApplicationProperties={isLoadingApplicationProperties}
          errors={errors}
        />
      </FieldRow>
      <FieldRow label="Property Value">
        <PropertyValue<{ propertyName: string } & ApplicationPropertyValPostUser>
          control={control}
          setValue={setValue}
          applicationRolePermissionPropertiesIds={userApplicationPermissionPropertiesIds}
          propertyPossibleValues={propertyPossibleValues}
          propertyType={propertyType}
          propertyPossibleValue={propertyPossibleValue}
          setPropertyPossibleValue={setPropertyPossibleValue}
          errors={errors}
        />
      </FieldRow>
    </FormPage>
  )
}
